import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import LeaderboardSummaryTable from '../components/LeaderboardSummaryTable';
import { DuplicateWarning } from '../components/LeaderboardFormFields';

import Button from '../../../shared/_TBComponents/Button';

import { saveTemplate } from '../ducks/leaderboardActions';
import { findDuplicateTemplate } from '../helpers';

const DEFAULT_INTRO = 'Save this leaderboard as a template so you can quickly load it again in the future.';

const ContentWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
`;

const Header = styled('div')`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const Subtitle = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #9e9e9e;
`;

const Intro = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  margin: 0;
`;

const SummaryWrapper = styled('div')`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 16px;
`;

const ErrorText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #c62828;
  margin: 16px 0 0;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const buttonTextProps = { fontWeight: '600', letterSpacing: '0px' };

const SaveDeleteTemplateModal = ({
  leaderboardId,
  templateId,
  title,
  boardNumber,
  closeModal,
  intro,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  primaryDanger,
}) => {
  const board = useSelector((state) => (
    leaderboardId ? state.leaderboard.data.leaderboards[leaderboardId] : null
  ));
  const template = useSelector((state) => (
    templateId ? state.leaderboard.data.templates.find((t) => t.id === templateId) : null
  ));
  const isTemplateSavingDeleting = useSelector(
    (state) => state.leaderboard.ui.isTemplateSavingDeleting,
  );
  const saveTemplateError = useSelector((state) => state.leaderboard.data.saveTemplateError);
  const templates = useSelector((state) => state.leaderboard.data.templates);
  const dispatch = useDispatch();

  if (!board && !template) return null;

  const duplicate = board && !primaryDanger
    ? findDuplicateTemplate(board.leaderboardForm, templates)
    : null;

  let category;
  let exerciseName;
  let groups;
  let mustBeInAllGroups;
  let gender;
  let rangeType;
  let compareTo;
  let rangeDates;
  let compareDates;

  if (board) {
    ({
      category, groups, mustBeInAllGroups, gender, rangeType, compareTo, rangeDates, compareDates,
    } = board.leaderboardForm);
    exerciseName = board.leaderboardExercise;
  } else {
    ({
      category, exerciseName, groups, mustBeInAllGroups, gender,
      rangeType, compareTo, rangeDates, compareDates,
    } = template);
  }

  const defaultHandleSave = async () => {
    if (!board) return;
    try {
      await dispatch(saveTemplate(board.leaderboardForm, leaderboardId));
      closeModal();
    } catch (err) {
      // saveTemplateError set in Redux via SAVE_TEMPLATE_REJECTED
    }
  };

  const handlePrimary = onPrimary || defaultHandleSave;

  return (
    <ContentWrapper>
      <Header>
        <Title>{title}</Title>
        {boardNumber > 0 && <Subtitle>{`(Leaderboard ${boardNumber})`}</Subtitle>}
      </Header>
      <Intro>{intro}</Intro>
      <SummaryWrapper>
        <LeaderboardSummaryTable
          category={category}
          exerciseName={exerciseName}
          groups={groups}
          mustBeInAllGroups={mustBeInAllGroups}
          gender={gender}
          rangeType={rangeType}
          rangeDates={rangeDates}
          compareTo={compareTo}
          compareDates={compareDates}
        />
      </SummaryWrapper>
      {saveTemplateError && <ErrorText>{saveTemplateError}</ErrorText>}
      {duplicate && <DuplicateWarning />}
      <ButtonContainer>
        <Button
          name={primaryLabel}
          bgColor={primaryDanger ? '#e53935' : '#444444'}
          txtColor='white'
          noBorder
          fullWidth
          fontSize='14px'
          customBorderRadius='4px'
          styledTextProps={buttonTextProps}
          onClick={handlePrimary}
          disabled={isTemplateSavingDeleting}
          type='button'
        />
        <Button
          name={secondaryLabel}
          bgColor='white'
          borderColor='#444444'
          txtColor='#444444'
          fullWidth
          fontSize='14px'
          customBorderRadius='4px'
          styledTextProps={buttonTextProps}
          onClick={closeModal}
          disabled={isTemplateSavingDeleting}
          type='button'
        />
      </ButtonContainer>
    </ContentWrapper>
  );
};

SaveDeleteTemplateModal.defaultProps = {
  leaderboardId: null,
  templateId: null,
  title: 'Save as Template',
  boardNumber: 0,
  intro: DEFAULT_INTRO,
  primaryLabel: 'Save as Template',
  onPrimary: null,
  secondaryLabel: 'Go Back',
  primaryDanger: false,
};

SaveDeleteTemplateModal.propTypes = {
  leaderboardId: PropTypes.number,
  templateId: PropTypes.number,
  title: PropTypes.string,
  boardNumber: PropTypes.number,
  closeModal: PropTypes.func.isRequired,
  intro: PropTypes.string,
  primaryLabel: PropTypes.string,
  onPrimary: PropTypes.func,
  secondaryLabel: PropTypes.string,
  primaryDanger: PropTypes.bool,
};

export default SaveDeleteTemplateModal;
